/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Container,
  Grid,
  Paper,
  Button,
  Typography,
  Link as StyledLink,
} from '@material-ui/core';
import {
  AddBox,
  ArrowDownward,
  Check,
  Clear,
  DeleteOutline,
  ChevronLeft,
  ChevronRight,
  Edit,
  SaveAlt,
  FilterList,
  FirstPage,
  LastPage,
  Search,
  Remove,
  ViewColumn,
} from '@material-ui/icons';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import SwipeableViews from 'react-swipeable-views';
import { setup } from 'axios-cache-adapter';
import {
  EventList,
  VenueList,
  PromoterList,
  PastEventList,
  RecurringEventList,
} from '../../components';
import { ConfigContext, Config } from '../../config';
import donateImage from '../../images/donate.jpg';

const tableIcons = {
  Add: React.forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: React.forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: React.forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: React.forwardRef((props, ref) => (
    <DeleteOutline {...props} ref={ref} />
  )),
  DetailPanel: React.forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Edit: React.forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: React.forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: React.forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: React.forwardRef((props, ref) => (
    <FirstPage {...props} ref={ref} />
  )),
  LastPage: React.forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: React.forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  PreviousPage: React.forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: React.forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: React.forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: React.forwardRef((props, ref) => (
    <ArrowDownward {...props} ref={ref} />
  )),
  ThirdStateCheck: React.forwardRef((props, ref) => (
    <Remove {...props} ref={ref} />
  )),
  ViewColumn: React.forwardRef((props, ref) => (
    <ViewColumn {...props} ref={ref} />
  )),
};

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    margin: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
  },
  eventNav: {
    marginTop: theme.spacing(1),
  },
  donateContainer: {
    '& form': {
      textAlign: 'center',
    },
  },
  donateImage: {
    maxWidth: '100%',
    height: 'auto',
    paddingLeft: '60px',
    paddingRight: '60px',
    backgroundColor: 'rgb(127, 127, 127)',
    borderRadius: '9px',
    margin: '0 auto',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '75%',
    },
  },
  buttonLinkAdd: {
    textDecoration: 'none',
    color: theme.palette.primary.contrastText,
  },
  linkAdd: {
    paddingLeft: '8px',
    fontSize: '.6em',
  },
  buttonLink: {
    textDecoration: 'none',
    color: theme.palette.text.primary,
  },
  tabPanel: {
    margin: theme.spacing(0),
    padding: theme.spacing(0),
  },
  navHeader: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.05em',
    },
  },
  navButton: {
    [theme.breakpoints.down('xs')]: {
      fontSize: '.7em',
    },
  },
}));

const api = setup({
  baseURL: Config.siteUrl,
  cache: {
    maxAge: 15 * 60 * 1000,
  },
});

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <Typography
      component="div"
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </Typography>
  );
}

const navButtons = [
  'Events List',
  'Venues List',
  'Promoter List',
  'Past Events',
  'Recurring Events',
];

export default function Events({ match }) {
  const theme = useTheme();
  const classes = useStyles();
  const config = React.useContext(ConfigContext);
  const region = config.regions.find(
    ({ slug }) => slug === match.params.regionName
  );

  // eslint-disable-next-line
  const tableOptions = {
    filtering: false,
    padding: 'dense',
    pageSize: 100,
    pageSizeOptions: [25, 50, 100, 250],
    rowStyle: (rowData, index) => {
      if (index % 2) {
        return { backgroundColor: theme.palette.background.default };
      }
      return {};
    },
  };

  const [value, setValue] = React.useState(0);

  const handleChange = (newValue) => () => {
    setValue(newValue);
  };

  function isDisabled(e) {
    return e === value;
  }

  // eslint-disable-next-line
  const handleChangeIndex = (index) => {
    setValue(index);
  };

  return (
    <Container maxWidth="lg" className="root">
      <Helmet>
        <meta charSet="utf-8" />
        <title>{region.name} | electronic music calendar</title>
        <link
          rel="canonical"
          href={`${config.siteUrl}/events/${match.params.regionName}`}
        />
      </Helmet>
      <Grid container>
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <Grid container className={classes.eventNav} spacing={2}>
              <Grid item md={9}>
                <Typography variant="h5" className={classes.navHeader}>
                  <StyledLink component={Link} to="/">
                    Home
                  </StyledLink>{' '}
                  &gt; {region.name}
                  <span className={classes.linkAdd}>
                    {' ('}
                    <StyledLink component={Link} to="../contact/add">
                      Add Event
                    </StyledLink>
                    )
                  </span>
                  {/* <Grid item>
                      <Button variant="contained" size="small" color="primary">
                        <Link className={classes.buttonLinkAdd} to="./add">
                          Add Event
                        </Link>
                      </Button>
                    </Grid> */}
                </Typography>
                <Grid container className={classes.eventNav} spacing={1}>
                  {navButtons.map((button, index) => (
                    <Grid item key={button}>
                      <Button
                        key={button}
                        onClick={handleChange(index)}
                        variant="outlined"
                        size="small"
                        className={classes.navButton}
                        disabled={isDisabled(index)}
                      >
                        {button}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
              <Grid item xs={12} md={3} className={classes.donateContainer}>
                <form
                  action="https://www.paypal.com/cgi-bin/webscr"
                  method="post"
                  target="_top"
                >
                  <input type="hidden" name="cmd" value="_s-xclick" />
                  <input
                    type="hidden"
                    name="hosted_button_id"
                    value="2M3VZZ5TRHXDJ"
                  />
                  <input
                    type="image"
                    src={donateImage}
                    className={classes.donateImage}
                    border="0"
                    bordername="submit"
                    alt="PayPal Tip Link"
                  />
                </form>
              </Grid>
            </Grid>
          </Paper>
          <SwipeableViews
            axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
            index={value}
            onChangeIndex={handleChangeIndex}
            className={classes.tabPanel}
          >
            <TabPanel value={value} index={0} dir={theme.direction}>
              <EventList
                region={region}
                api={api}
                icons={tableIcons}
                options={tableOptions}
              />
            </TabPanel>
            <TabPanel value={value} index={1} dir={theme.direction}>
              <VenueList
                region={region}
                api={api}
                icons={tableIcons}
                options={tableOptions}
              />
            </TabPanel>
            <TabPanel value={value} index={2} dir={theme.direction}>
              <PromoterList
                region={region}
                api={api}
                icons={tableIcons}
                options={tableOptions}
              />
            </TabPanel>
            <TabPanel value={value} index={3} dir={theme.direction}>
              <PastEventList
                region={region}
                api={api}
                icons={tableIcons}
                options={tableOptions}
              />
            </TabPanel>
            <TabPanel value={value} index={4} dir={theme.direction}>
              <RecurringEventList
                region={region}
                api={api}
                icons={tableIcons}
                options={tableOptions}
              />
            </TabPanel>
          </SwipeableViews>
        </Grid>
      </Grid>
    </Container>
  );
}

Events.propTypes = {
  match: PropTypes.object,
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};
